import { poseAt } from "../game/motion";
import type { Pose, Spot } from "../game/motion";

// One tile's flight (design 10 §7): everything about it in one object, so that it starts, ends and is cancelled as one.
//
// The element's own style always says where the tile rests: its place, its picture, its order. A flight only overrides that
// while it lasts, so whatever ends it the tile is right, with nothing to put back and no timer to outlive it. The path
// is an animation of the transform, which the compositor runs. What changes only once (the picture it set out with, its order
// on the way) is held by animations of their own, kept apart so that the path stays on the compositor. Each lasts a
// millisecond after a delay, and holds its value by filling backwards: waiting costs nothing, as the browser has no frame to
// sample until it ends. All of them start at one stated time: left to start themselves the path, on the compositor, starts a
// little after the others (~110 ms at a browser's first table), and the tile turns over beside the wall.

export const FLYING_Z = 1000000;

export interface FlightSpec {
  path: readonly Pose[];
  frames: Keyframe[];
  ms: number;
  delay: number;
  picture?: Keyframe; // the picture it shows until the middle of its flight
  waitZ?: string; // its order while it waits for its delay
  onLand: () => void;
}

export class Flight {
  private anim!: Animation;
  private path: readonly Pose[] = [];
  private readonly holds: Animation[] = [];
  private readonly start = document.timeline.currentTime;

  constructor(
    private readonly el: HTMLElement,
    private readonly spec: FlightSpec,
  ) {
    // The later wins: its order while it waits over its order on the way.
    this.holds.push(this.hold({ zIndex: String(FLYING_Z) }, spec.delay + spec.ms));
    if (spec.delay && spec.waitZ) this.holds.push(this.hold({ zIndex: spec.waitZ }, spec.delay - 1));
    if (spec.picture) this.holds.push(this.hold(spec.picture, spec.delay + spec.ms / 2));
    this.fly(spec.path, spec.frames);
  }

  // The page's timeline, by name: a tile made in this same update is not in the page yet, and an element cloned from
  // a template still belongs to the template's inert document, whose timeline never runs. Left to choose, its flight
  // would wait there for good, the tile frozen on its first frame in the wall (owner, 2026-10-31).
  private play(frames: Keyframe[], duration: number, delay: number): Animation {
    const a = this.el.animate(frames, { duration, delay, fill: "backwards", timeline: document.timeline });
    a.startTime = this.start;
    return a;
  }

  private hold(values: Keyframe, until: number): Animation {
    return this.play([values, values], 1, until);
  }

  private fly(path: readonly Pose[], frames: Keyframe[]): void {
    this.path = path;
    this.anim = this.play(frames, this.spec.ms, this.spec.delay);
    this.anim.onfinish = this.spec.onLand;
  }

  /** The same flight, landing elsewhere: its timing and its holds go on as they were. */
  aim(path: readonly Pose[], frames: Keyframe[]): void {
    this.anim.onfinish = null;
    this.anim.cancel();
    this.fly(path, frames);
  }

  get running(): boolean {
    return this.anim.playState === "running" || this.anim.playState === "paused";
  }

  /** Where its tile is now, without asking the browser: asking for style recomputes it, on a phone, at every interruption. */
  get pose(): Spot {
    return poseAt(this.path, this.spec.ms, this.spec.delay, Number(this.anim.currentTime ?? 0));
  }

  /** Ends it, not landing: the tile is at its rest. */
  cancel(): void {
    this.anim.onfinish = null;
    for (const h of this.holds) h.cancel();
    this.anim.cancel();
  }
}
